// src/pages/HeldOrdersPage.jsx
import React, { useState, useEffect } from 'react';
import styles from './HeldOrdersPage.module.css';
import { getHeldOrders, releaseHeldOrder, unholdHeldOrder, cancelHeldOrder } from '../api/salesRequestApi';
import masaLogo from '../Assets/MasaLogo.png';

const HeldOrdersPage = () => {
  const [heldOrders, setHeldOrders] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  useEffect(() => {
    fetchHeldOrders();
  }, []);

  const fetchHeldOrders = async () => {
    try {
      const response = await getHeldOrders();
      setHeldOrders(response.data);
    } catch (error) {
      console.error('Error fetching held orders:', error);
      setModalMessage('Failed to fetch held orders. Please try again.');
      setShowModal(true);
    }
  };

  const handleRelease = async (id, action) => {
    if (action !== 'remove') {
      setModalMessage('Release action is only applicable for removal requests.');
      setShowModal(true);
      return;
    }

    if (window.confirm('Are you sure you want to release this order now?')) {
      try {
        await releaseHeldOrder(id);
        // Remove the released order from heldOrders
        setHeldOrders(prev => prev.filter(order => order._id !== id));
        setModalMessage('Held order released successfully.');
        setShowModal(true);
      } catch (error) {
        console.error('Error releasing held order:', error);
        setModalMessage('Failed to release held order. Please try again.');
        setShowModal(true);
      }
    }
  };

  const handleUnhold = async (id, action) => {
    if (action !== 'remove') {
      setModalMessage('Unhold action is only applicable for removal requests.');
      setShowModal(true);
      return;
    }

    if (window.confirm('Are you sure you want to unhold this order?')) {
      try {
        await unholdHeldOrder(id);
        // Remove the unheld order from heldOrders
        setHeldOrders(prev => prev.filter(order => order._id !== id));
        setModalMessage('Held order unheld successfully.');
        setShowModal(true);
      } catch (error) {
        console.error('Error unholding held order:', error);
        setModalMessage('Failed to unhold held order. Please try again.');
        setShowModal(true);
      }
    }
  };

  const handleCancel = async (id, action) => {
    if (action !== 'remove') {
      setModalMessage('Cancel action is only applicable for removal requests.');
      setShowModal(true);
      return;
    }

    const reason = prompt('Enter reason for cancellation:');
    if (!reason) {
      setModalMessage('Cancellation reason is required.');
      setShowModal(true);
      return;
    }
    if (window.confirm('Are you sure you want to cancel this order?')) {
      try {
        await cancelHeldOrder(id, { reason });
        // Remove the cancelled order from heldOrders
        setHeldOrders(prev => prev.filter(order => order._id !== id));
        setModalMessage('Held order cancelled successfully.');
        setShowModal(true);
      } catch (error) {
        console.error('Error cancelling held order:', error);
        setModalMessage('Failed to cancel held order. Please try again.');
        setShowModal(true);
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalMessage('');
  };

  return (
    <main className={styles.heldOrdersContainer}>
      <header className={styles.header}>
        <img loading="lazy" src={masaLogo} className={styles.logo} alt="Logo" />
        <h1 className={styles.title}>Held Orders</h1>
      </header>
      <div className={styles.explanationText}>
        <p>Manage all held sales orders here. You can unhold, release, or cancel each held order as needed.</p>
      </div>
      {errorMessage && (
        <div className={styles.errorBanner}>{errorMessage}</div>
      )}
      <section className={styles.tableContainer}>
        {heldOrders.length === 0 ? (
          <p>No held sales requests.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Request ID</th>
                <th>Product</th>
                <th>Sort</th>
                <th>Color</th>
                <th>Action</th>
                <th>Quantity</th>
                <th>Submission Date</th>
                <th>Sales User</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {heldOrders.map((order) => (
                <tr key={order._id}>
                  <td>{order._id}</td>
                  <td>{order.product}</td>
                  <td>{order.sortName}</td>
                  <td>{order.color}</td>
                  <td>{order.action}</td>
                  <td>{order.quantity}</td>
                  <td>{new Date(order.submissionDate).toLocaleDateString()}</td>
                  <td>{order.salesUser?.username || 'N/A'}</td>
                  <td>
                    {order.action === 'remove' ? (
                      <>
                        <button 
                          className={styles.releaseButton} 
                          onClick={() => handleRelease(order._id, order.action)}
                        >
                          Release Now
                        </button>
                        <button 
                          className={styles.unholdButton} 
                          onClick={() => handleUnhold(order._id, order.action)}
                        >
                          Unhold
                        </button>
                        <button 
                          className={styles.cancelButton} 
                          onClick={() => handleCancel(order._id, order.action)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <p>No actions available for add requests.</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Modal for Messages */}
      {showModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <p>{modalMessage}</p>
            <button className={styles.closeButton} onClick={closeModal}>
              Close
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default HeldOrdersPage;
